import { useState, useEffect } from "react";
import { ordersAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ShoppingBag, Loader2, ChevronLeft, ChevronRight, User, ArrowUpRight } from "lucide-react";

function formatTime(iso) { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }); }

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = async (p = page, d = date) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 15 };
      if (d) params.date = d;
      const res = await ordersAPI.list(params);
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleDateChange = (e) => {
    setDate(e.target.value);
    setPage(1);
    fetchOrders(1, e.target.value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchOrders(newPage, date);
  };

  return (
    <div className="space-y-5 fade-in" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Orders</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={handleDateChange} className="w-40 h-9 text-sm bg-white" data-testid="orders-date-filter" />
          {date && <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setDate(""); setPage(1); fetchOrders(1, ""); }} data-testid="orders-clear-filter">Clear</Button>}
        </div>
      </div>

      <Card className="border-slate-100" data-testid="orders-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : orders.length === 0 ? (
            <div className="py-20 text-center" data-testid="orders-empty-state">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <ShoppingBag className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm text-slate-600 font-medium">No orders created yet</p>
              <p className="text-xs text-slate-400 mt-1">Orders will appear after AI confirms and pushes to POS.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold pl-5">Time</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Customer</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Items</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">POS ID</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Status</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold text-right pr-5"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow key={order.id} className="cursor-pointer table-row-hover border-slate-50" onClick={() => setSelectedOrder(order)} data-testid={`order-row-${order.id}`}>
                        <TableCell className="text-[13px] text-slate-600 pl-5 whitespace-nowrap py-3">{formatTime(order.created_at)}</TableCell>
                        <TableCell className="text-[13px] font-medium text-slate-800">{order.customer_name}</TableCell>
                        <TableCell className="text-[13px] text-slate-500">{order.items?.length || 0} items</TableCell>
                        <TableCell className="text-[13px] font-semibold text-slate-800 tabular-nums">${order.total?.toFixed(2)}</TableCell>
                        <TableCell className="text-[12px] font-mono text-slate-400">{order.pos_order_id}</TableCell>
                        <TableCell><Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 badge-pill">{order.status}</Badge></TableCell>
                        <TableCell className="text-right pr-5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-primary" data-testid={`view-order-${order.id}`}><ArrowUpRight className="w-3.5 h-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100" data-testid="orders-pagination">
                  <span className="text-[12px] text-slate-400">Page {page} of {pages}</span>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => handlePageChange(page - 1)} data-testid="orders-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= pages} onClick={() => handlePageChange(page + 1)} data-testid="orders-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Modal */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-md" data-testid="order-detail-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Customer</p>
                <p className="text-sm font-medium flex items-center gap-1"><User className="w-3 h-3" /> {selectedOrder.customer_name}</p>
                <p className="text-xs text-muted-foreground font-mono">{selectedOrder.customer_phone}</p>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Items</p>
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <div>
                      <span>{item.quantity}x {item.name}</span>
                      {item.modifiers?.length > 0 && <span className="text-xs text-muted-foreground ml-1">({item.modifiers.join(", ")})</span>}
                    </div>
                    <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <Separator />
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>${selectedOrder.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tax</span><span>${selectedOrder.tax?.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-base"><span>Total</span><span>${selectedOrder.total?.toFixed(2)}</span></div>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-muted-foreground">POS Order ID</p>
                  <p className="text-sm font-mono">{selectedOrder.pos_order_id}</p>
                </div>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{selectedOrder.status}</Badge>
              </div>
              {selectedOrder.sms_sent && (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">SMS: {selectedOrder.sms_status}</Badge>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
